/**
 * Regelversion 2:
 *  - Vorhaben aus den Meinungsthemen (content/agenda.ts)
 *  - Der Meinungstrend wirkt beim Inkrafttreten eines Gesetzes auf die Zustimmung
 *  - Gegenfinanzierung als Stellschraube im Entwurf
 * Alles hier greift nur, wenn `since(2)` gilt. Partien nach Version 1 bleiben unverändert.
 */
import { AGENDA, V2POS, type AgendaItem } from '../content/agenda';
import { AX, AXN } from '../content/axes';
import { GROUPS } from '../content/axes';
import { TPL } from '../content/issues';
import { ISS } from '../content/states';
import { pub } from './opinion';
import { S, log } from './state';
import { clamp, de } from './util';
import { since } from './version';
import { axes } from './axes';
import { applyRich } from './rich';
import { applyBurden } from './burden8';
import { applyGini } from './fair';
import { baseLaw, novStart } from './novelle9';

// ── Gegenfinanzierung ──

/** Optionen der Gegenfinanzierung. Index 0 = über Schulden (wie bisher). */
export const FIN_OPTS = ['Über Schulden', 'Subventionen streichen', 'Spitzensteuersatz anheben', 'Vermögende stärker belasten'];
/** Parameter-Name im Entwurf. Unterstrich, damit er mit keiner Stellschraube einer Vorlage kollidiert. */
export const FIN_KEY = '_gf';
/** Ab diesen jährlichen Kosten bietet der Entwurf eine Gegenfinanzierung an. */
export const FIN_MIN = .5;

/** Hat die Vorlage schon eine eigene Finanzierungs-Stellschraube? Dann keine zweite anbieten. */
export const ownFinancing = (T: any) => (T.ps || []).some((p: any) => p.id === 'fin');

function applyFinancing(fx: any, opt: number) {
  const c = Math.max(0, -(fx.b || 0));
  if (!opt || c < FIN_MIN) return fx;
  const g = { ...(fx.g || {}) }, pos = { ...(fx.pos || {}) };
  const add = (k: string, v: number) => { g[k] = (g[k] || 0) + v; };
  let gr = fx.gr || 0;
  if (opt === 1) { add('unt', -c * .5); add('arb', -c * .2); add('rent', -c * .1); gr -= c * .004; pos.buero = (pos.buero || 0) - .3; }
  if (opt === 2) { add('unt', -c * .35); add('arb', -c * .15); gr -= c * .006; pos.steuern = (pos.steuern || 0) + .3 + c * .05; }
  if (opt === 3) { add('unt', -c * .25); add('rent', -c * .1); gr -= c * .003; pos.steuern = (pos.steuern || 0) + .6 + c * .08; }
  return { ...fx, b: (fx.b || 0) + c, g, gr, pos, fin: { opt, c } };
}

/**
 * Wirkung eines Entwurfs nach den Regeln der laufenden Partie.
 * Version 1: genau `T.fx(p)`. Version 2: zusätzlich Programm-Positionen und Gegenfinanzierung.
 */
export function lawFx(T: any, p: any, except?: number | null) {
  const fx = T.fx(p);
  if (!since(2)) return fx;
  const extra = V2POS[T.t]?.(p);
  const withPos = extra ? { ...fx, pos: { ...(fx.pos || {}), ...extra } } : fx;
  const fin = ownFinancing(T) ? withPos : applyFinancing(withPos, Number(p?.[FIN_KEY]) || 0);
  const rich = applyRich(T, p, fin, except); // Version 7: Vermögende sind begrenzt belastbar
  return applyGini(applyBurden(T, p, rich, except)); // Version 8: Unternehmen, Arbeitnehmer, soziale Schere
}

// ── Meinungstrend ──

export type ZgPart = { a: string; n: string; law: number; pub: number; d: number };
/** Wie gut passt ein Gesetz zur Stimmung im Land? `tot` > 0: kommt gut an. */
export function zgFit(pos: Record<string, number>): { tot: number; parts: ZgPart[] } {
  const parts: ZgPart[] = [];
  for (const a of axes()) {
    const v = pos?.[a] || 0; if (Math.abs(v) < .3) continue;
    const pv = pub(a), d = clamp(v, -2, 2) * pv / 2;
    if (Math.abs(d) >= .05) parts.push({ a, n: AXN[a], law: v, pub: pv, d });
  }
  parts.sort((x, y) => Math.abs(y.d) - Math.abs(x.d));
  return { tot: clamp(parts.reduce((s, x) => s + x.d, 0), -3, 3), parts };
}
/** Stärke der Wirkung auf die Stimmung aller Gruppen je Punkt Passung. */
export const ZG_WEIGHT = 1.5;

/** Gilt bereits ein Gesetz mit diesem Titel (in Kraft oder in der Einführung)? */
export const inForce = (t: string, except?: any) => (S.laws || []).some((L: any) => L !== except && L.t === t && (L.stage === 'kraft' || L.stage === 'done') && !L.rep);

/** Beim Inkrafttreten: Stimmung im Land auf die Zustimmung wirken lassen. */
export function zgEnact(L: any) {
  if (!since(2)) return;
  if (inForce(L.t, L)) return; // dasselbe Gesetz noch einmal: kein zweiter Stimmungsbonus
  const { tot, parts } = zgFit(L.fx?.pos || {});
  if (Math.abs(tot) < .2) return;
  for (const g in GROUPS) S.bonus[g] = (S.bonus[g] || 0) + tot * ZG_WEIGHT;
  L.zg = Math.round(tot * 10) / 10;
  const top = parts[0]?.n;
  if (tot > 0) log(`„${L.t}“ trifft die Stimmung im Land${top ? ` (${top})` : ''}. Die Zustimmung steigt.`, 'good');
  else log(`„${L.t}“ geht an der Stimmung im Land vorbei${top ? ` (${top})` : ''}. Das kostet Zustimmung.`, 'bad');
}

/** Zusätzliche Stimmen in der Anhörung (Version 2). */
export function hearingV2(L: any): { who: string; txt: string; tone: number }[] {
  if (!since(2)) return [];
  const out: { who: string; txt: string; tone: number }[] = [];
  const f = L.fx || {};
  if (f.fin && f.fin.opt) out.push({ who: 'Bundesrechnungshof', txt: `Die Kosten von ${de(f.fin.c, 1)} Mrd € pro Jahr sind gegenfinanziert: ${FIN_OPTS[f.fin.opt]}.`, tone: .5 });
  const { tot } = zgFit(f.pos || {});
  if (tot >= 1) out.push({ who: 'Meinungsforscher', txt: 'Eine klare Mehrheit der Bevölkerung unterstützt das Vorhaben.', tone: .5 });
  else if (tot <= -1) out.push({ who: 'Meinungsforscher', txt: 'Eine Mehrheit der Bevölkerung lehnt das Vorhaben ab.', tone: -.5 });
  return out;
}

// ── Vorhaben aus den Meinungsthemen ──

export type AgendaView = { ax: string; i: number; it: AgendaItem; T: any; dir: number; busy: boolean; done: boolean; home: string };
/** Vorhaben zu einem Meinungsthema, mit Richtung (+1 dafür, −1 dagegen) und ob das Thema schon belegt ist. */
export function agendaFor(ax: string): AgendaView[] {
  return (AGENDA[ax] || []).map((it, i) => {
    const T = TPL(it.iss, it.k);
    const p = { ...Object.fromEntries(T.ps.map((x: any) => [x.id, x.d])), ...(it.set || {}) };
    let v = 0; try { v = lawFx(T, p).pos?.[ax] || 0; } catch { /* Vorlage ohne Wirkung */ }
    const st = S.issues?.[it.iss];
    return { ax, i, it, T, dir: v >= 0 ? 1 : -1, busy: !!(st && st.law), done: inForce(T.t) && !(since(9) && agendaTurn(it)), home: ISS[it.iss]?.agenda ? '' : ISS[it.iss]?.n ?? '' };
  });
}

/** Version 9: Würde das Vorhaben ein geltendes Gesetz derselben Vorlage in eine andere Richtung ändern? */
export function agendaTurn(it: AgendaItem): boolean {
  const B = baseLaw(it.iss, it.k); if (!B || !it.set) return false;
  return Object.entries(it.set).some(([k, v]) => (B.p as any)?.[k] !== v);
}

/** Entwurf für ein Vorhaben öffnen (Spielzug `draftAgenda`). */
export function openAgendaDraft(ax: string, i: number) {
  if (!since(2) || S.phase !== 'govern') return false;
  const it = AGENDA[ax]?.[i]; if (!it) return false;
  const T = TPL(it.iss, it.k); if (!T) return false;
  if (!S.issues[it.iss]) S.issues[it.iss] = { sev: 0, prev: 0, cut: 0, law: null, on: false } as any;
  if (S.issues[it.iss].law) return false;
  if (inForce(T.t) && !(since(9) && agendaTurn(it))) return false; // Version 9: Kurswechsel als Novelle erlaubt
  const p0 = { ...Object.fromEntries(T.ps.map((x: any) => [x.id, x.d])), ...(it.set || {}) };
  S.editing = { iss: it.iss, tpl: it.k, lawId: null, p: since(9) ? { ...novStart(it.iss, it.k, p0), ...(it.set || {}) } : p0, ax } as any;
  return true;
}
